import { isVideoThumbnailUrl } from '@ps/functions/clips/popclip-thumbnail';
import { PopImage } from '@ps/ui/atoms/pop-image';
import { VideoPoster } from '@ps/ui/atoms/video-poster';
import { Play, Zap } from 'lucide-react';

import type {
  SeriesClipBadgeViewModel,
  SeriesClipViewModel,
} from '../types/SeriesClipViewModel';
import { ClipLockOverlay } from './ClipLockOverlay';

interface Props {
  clip: SeriesClipViewModel;
  /**
   * Pixel resolution to request the thumbnail at (the resize-service target).
   * The cell's RENDERED width is responsive — the parent rail sizes each cell
   * as a fraction of its own width — so this only tunes thumbnail crispness.
   */
  thumbnailWidth: number;
  /**
   * Resolved title style (from {@link bioItemOverlayTextStyle}) so the clip
   * title picks up the creator's `font_config` — 1:1 with legacy
   * `Typography.BodyText`. Omit and the title falls back to the baked 12px
   * white style (callers not yet threading appearance).
   */
  titleStyle?: import('react').CSSProperties;
}

/** Clip thumbnails are a 2:3 portrait cell — the resize service crops to match. */
const THUMBNAIL_ASPECT = 3 / 2;

/**
 * One clip thumbnail cell — thumbnail + content-type badge + optional chapter
 * labels + a visitor-facing locked overlay. Pure, intent-agnostic, server-
 * renderable: props-only, no context, no `'use client'`. The interactive tap
 * (open the player) is the parent island's concern — this just paints the cell.
 *
 * Faithful re-implementation of legacy `SeriesElementCell` (`SeriesView.tsx:496-885`),
 * minus the container-proportional pixel math (the parent grid sizes the cell
 * via the `width` prop + a CSS aspect ratio) and the owner/editable menu (that's
 * the editable variant's slot, not this pure cell's concern).
 */
export function SeriesClipCellView({
  clip,
  thumbnailWidth,
  titleStyle,
}: Props) {
  return (
    <div
      className="relative w-full text-left"
      style={{ aspectRatio: '2 / 3', scrollSnapAlign: 'start' }}
    >
      <div className="relative h-full w-full overflow-hidden rounded-t-lg">
        {clip.thumbnailUrl ? (
          isVideoThumbnailUrl(clip.thumbnailUrl) ? (
            // The resolved thumbnail is a video cover (`.mp4`) — render its
            // first frame in a <video>, not an <img>. 1:1 with RN's
            // `VideoCover` branch in `SeriesElementListItem`.
            <VideoPoster src={clip.thumbnailUrl} aria-label={clip.title} />
          ) : (
            <PopImage
              src={clip.thumbnailUrl}
              alt={clip.title}
              fill
              resizing={{
                width: Math.round(thumbnailWidth),
                height: Math.round(thumbnailWidth * THUMBNAIL_ASPECT),
                fit: 'cover',
              }}
              sizes={`${Math.round(thumbnailWidth)}px`}
            />
          )
        ) : (
          <div className="bg-scrim/10 dark:bg-raise/10 flex h-full w-full items-center justify-center">
            <div className="bg-surface-canvas dark:bg-raise/20 h-8 w-8 rounded" />
          </div>
        )}

        {/* Lock visuals — VIEWER-AWARE via a client leaf. The baked
            `showUnlockOverlay` is computed server-side with no viewer standing;
            `ClipLockOverlay` hydrates, reads the viewer's subscription/follow
            standing, and only paints the lock when the clip is actually locked
            for THIS viewer (an entitled subscriber sees the clean thumbnail). */}
        <ClipLockOverlay
          accessLevel={clip.accessLevel}
          showLockedThumbnail={clip.showUnlockOverlay}
          courseListingId={clip.courseListingId}
        />

        {/* Vertical chapter-name label — right edge, first clip of a chapter.
            The gutter starts BELOW the top-right ⋮ menu (owner rails overlay
            it there via `renderClip`) so the rotated, bottom-anchored text
            can't grow up under the menu. `writing-mode: vertical-rl` ignores
            CSS ellipsis + `max-height`, so the name is hard char-truncated in
            JS instead (QA: chapter name overlapped the menu). */}
        {clip.chapterName ? (
          <div
            className="pointer-events-none absolute bottom-0 right-0 top-10 z-[6] flex w-[26px] items-end justify-center overflow-hidden pb-2"
            style={{
              background:
                'linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,0.82))',
            }}
          >
            <span
              className="text-content-1 font-medium leading-none"
              style={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                fontSize: 9,
                whiteSpace: 'nowrap',
                textShadow: '0 1px 1px rgba(0,0,0,0.5)',
              }}
            >
              {clip.chapterName.length > 20
                ? `${clip.chapterName.slice(0, 20).trimEnd()}…`
                : clip.chapterName}
            </span>
          </div>
        ) : null}

        {/* Chapter label — top-right (e.g. "ch. 1", "Chapter 2"). */}
        {clip.chapterLabel ? (
          <div className="text-content-1 absolute right-0 top-0 z-[7] rounded-bl-lg px-2 py-0.5 text-[10px] font-medium">
            {clip.chapterLabel}
          </div>
        ) : null}

        {/* Episode / Teaser / Promo / Sponsored badge — left side, near bottom. */}
        {clip.badge ? <ClipBadge badge={clip.badge} /> : null}

        {/* Bottom gradient + title overlay. */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-9"
          style={{
            background:
              'linear-gradient(to top, rgba(0,0,0,0.4), rgba(0,0,0,0))',
          }}
        />
        {clip.title ? (
          <div className="absolute inset-x-1.5 bottom-1.5 z-[6]">
            {/* Title picks up the creator's `font_config` via `titleStyle`
                (1:1 legacy `Typography.BodyText`); falls back to the baked
                12px white style when a caller doesn't thread appearance. */}
            <p
              className={
                titleStyle ? 'truncate' : 'text-content-1 truncate text-[12px]'
              }
              style={{
                ...titleStyle,
                textShadow: '1px 1px 1px rgba(0,0,0,0.5)',
              }}
            >
              {clip.title}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const BADGE_BASE_CLASS =
  'absolute bottom-[18%] left-0 z-[15] flex items-center gap-0.5 rounded-r px-1 py-1 text-[10px] font-semibold';

/** The four content-type badges — 1:1 colors/icons with legacy `EpisodeTeaserBadge`. */
function ClipBadge({ badge }: { badge: SeriesClipBadgeViewModel }) {
  if (badge.kind === 'teaser') {
    return (
      <div
        className={`${BADGE_BASE_CLASS} text-black`}
        style={{ backgroundColor: '#CCFF00' }}
      >
        <Zap className="h-2.5 w-2.5 fill-current" strokeWidth={0} aria-hidden />
        {badge.label}
      </div>
    );
  }
  if (badge.kind === 'promo') {
    return (
      <div
        className={`${BADGE_BASE_CLASS} text-content-1`}
        style={{
          background: 'linear-gradient(to right, #FF61F4, hsl(var(--danger)))',
        }}
      >
        <Play
          className="h-2.5 w-2.5 fill-current"
          strokeWidth={0}
          aria-hidden
        />
        {badge.label}
      </div>
    );
  }
  if (badge.kind === 'sponsored') {
    return (
      <div
        className={`${BADGE_BASE_CLASS} text-black`}
        style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
      >
        {badge.label}
      </div>
    );
  }
  // Episode (default) — filled play triangle + `ep.` / `episode` + sequence.
  return (
    <div className={`${BADGE_BASE_CLASS} bg-white text-black`}>
      <Play className="h-2.5 w-2.5 fill-current" strokeWidth={0} aria-hidden />
      {badge.label}
    </div>
  );
}
